'use client';

import { useCartAddressFormStore } from 'stores';
import { useRef, type KeyboardEvent } from 'react';
import type { CeweAddress } from 'services';
import { ADDRESS_FORM_FIELD_NAMES } from '../../constants';
import type { CartAddressFormShellProps } from '../../types';
import styles from '../../CartAddressStep.module.css';
import { useCartAddressStepSave } from './hooks/useCartAddressStepSave';
import { CartStepLayout } from '../../../partials/CartStepLayout';
import { BackButton } from '../../../partials/BackButton';
import { ProceedButton } from '../../../partials/ProceedButtonStream/ProceedButton';
import { AddressValidationDialog } from '../address-validation/AddressValidationDialog';
import { trackBackToOverviewClick } from '../../../analytics/address';

// Prevent Enter on a checkbox from submitting the entire address form instead of only changing the checkbox state.
const preventCheckboxSubmit = (event: KeyboardEvent<HTMLFormElement>) => {
  if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
    event.preventDefault();
  }
};

/**
 * Provides address state and the shared cart address form layout.
 * @param props - Device, localized constants, return route, and address form content.
 * @returns The shared address form shell with navigation actions.
 */
export function CartAddressFormShell({ constants, device, routes, children }: CartAddressFormShellProps) {
  const destinationType = useCartAddressFormStore(state => state.destinationType);
  const billingMatchesShipping = useCartAddressFormStore(state => state.billingMatchesShipping);
  const formRef = useRef<HTMLFormElement>(null);
  const { formAction, handleSubmit, isPending, ceweDialogOpen, ceweSuggestion, closeCeweDialog, submitWithAddress } =
    useCartAddressStepSave({
      constants,
      paymentRoute: routes.payment,
      formRef
    });

  const handleAddressSelection = (address: CeweAddress) => {
    const form = formRef.current;

    if (form) {
      submitWithAddress(form, address);
    }
  };

  const continueButton = (
    <ProceedButton isBusy={isPending} type="submit">
      {constants['i18n_continue-to-payment']}
    </ProceedButton>
  );

  return (
    <>
      <form
        ref={formRef}
        id="cart-address-form"
        action={formAction}
        onKeyDown={preventCheckboxSubmit}
        onSubmit={handleSubmit}
        className={styles.cartAddressStep}
      >
        <input name={ADDRESS_FORM_FIELD_NAMES.DESTINATION_TYPE} type="hidden" value={destinationType} />
        <input name="billingMatchesShipping" type="hidden" value={String(billingMatchesShipping)} />
        <input name="skipCeweAddressValidation" type="hidden" value="false" />
        <CartStepLayout
          title={constants['i18n_please-give-your-address']}
          stepHeader={continueButton}
          stepFooter={
            <>
              {continueButton}
              <BackButton
                label={constants['i18n_back-to-cart']}
                href={routes.overview}
                onClick={trackBackToOverviewClick}
              />
            </>
          }
        >
          {children}
        </CartStepLayout>
      </form>
      {ceweSuggestion && (
        <AddressValidationDialog
          device={device}
          open={ceweDialogOpen}
          comparison={ceweSuggestion}
          headingLabel={constants['i18n_check_your_shipping_address']}
          subtitleLabel={constants['i18n_select_correct_address']}
          suggestionCardLabel={constants['i18n_our_suggestion']}
          originalCardLabel={constants['i18n_your_input']}
          acceptAddressLabel={constants['i18n_accept_address']}
          closeAriaLabel={constants['i18n_close']}
          onAcceptSuggestion={() => handleAddressSelection(ceweSuggestion.suggested)}
          onKeepOriginal={() => handleAddressSelection(ceweSuggestion.original)}
          onClose={closeCeweDialog}
        />
      )}
    </>
  );
}
